import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    CircularProgress,
    Tooltip,
    Divider,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Popover,
    Paper,
    Grid,
    Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import DashboardBreadcrumbs from '../../components/DashboardBreadcrumbs';
import sessionService from '../../services/session.service';
import apiService from '../../services/api.service';
import { AuthContext } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { Icon as OpusIcon, Button as OpusButton, SearchInput as OpusSearchInput, LoadingState } from '../../components/common/opus';
import { Badge } from '../../components/common/opus';
import { Select as OpusSelect } from '../../components/common/opus';
import { useAlert } from '../../contexts/AlertContext';
import PlsCompetitionFixturePicker from '../../components/Renderer/PlsCompetitionFixturePicker';
import { createDdsPlsCatalogue } from '../../services/data/adapters/DdsPlsCatalogue';
import { getDDSClient } from '../../lib/ddsClient';
import { toDateTimeLocalValue } from '../../utils/dateTimeLocal';
import { SESSION_NAME_MAX_LENGTH, getSessionDisplayName, validateSessionName } from '../../utils/sessionName';
import EditSessionDialog from '../../components/Session/EditSessionDialog';
import MaestroSetupFields from '../../components/Session/MaestroSetupFields';
import {
    supportsMaestro,
    maestroFormFromSession,
    previewRendererUrlFor,
    retargetPreviewUrl,
    toMaestroPayload,
    validateMaestroForm,
    fieldsForFlavour,
} from '../../utils/maestroSettings';
import { useSessionFields } from '../../hooks/useSessionFields';

const FLAVOUR_LABELS = {
    aedrive: 'AeDrive',
    aether: 'Aether',
    copilot: 'Copilot',
    monitor: 'Monitor',
    custom: 'Custom',
};

const FLAVOUR_TONE = {
    aedrive: 'blue',
    aether: 'green',
    copilot: 'violet',
    monitor: 'amber',
    custom: 'neutral',
};

// Paired fields sit side by side once the dialog is wide enough.
const TWO_COLUMNS_SX = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'start' };

// Initial localStorage-seeded defaults for the create-dialog's PLS
// competition/season browse state (see plsCompetitionId/plsSeasonId below).
// Sessions persist whichever competition/season the operator actually
// picks in applicationDetails, not these constants.
const PLS_COMPETITION_ID = '2kwbbcootiqqgmrzs6o5inle5';
const PLS_SEASON_ID = '51r6ph2woavlbbpk8f29nynf8';

const SESSION_BASE_URL = process.env.REACT_APP_SESSION_API_URL || 'http://localhost:3005';
const buildShortUrl = (slug) => `${SESSION_BASE_URL}/${slug}`;

// ─── Short URL cell ───────────────────────────────────────────────────────────
const ShortUrlCell = ({ session, isElevated, onAssigned, onUnassigned, onDeleted, onCopied, onError }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [newAlias, setNewAlias] = useState('');
    const [existingAliases, setExistingAliases] = useState([]);
    const [loadingAliases, setLoadingAliases] = useState(false);
    const [selectedExisting, setSelectedExisting] = useState('');
    const [saving, setSaving] = useState(false);
    const [unassigning, setUnassigning] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const open = Boolean(anchorEl);
    const currentAlias = session.shortUrl;
    const displayName = session.shortUrlLabel || currentAlias;

    const handleOpen = async (e) => {
        e.stopPropagation();
        setNewAlias('');
        setSelectedExisting('');
        setAnchorEl(e.currentTarget);
        setLoadingAliases(true);
        try {
            const res = await sessionService.listShortUrlAliases({ available: true });
            const available = res.data || [];
            // Include current alias so user can re-select it
            if (currentAlias && !available.find((a) => a.slug === currentAlias)) {
                available.unshift({ slug: currentAlias, label: session.shortUrlLabel, assignedSessionId: session.sessionId });
            }
            setExistingAliases(available);
        } catch {
            setExistingAliases([]);
        } finally {
            setLoadingAliases(false);
        }
    };

    const handleClose = () => setAnchorEl(null);

    const handleAssign = async () => {
        const slug = (selectedExisting || newAlias).trim();
        if (!slug) return;
        setSaving(true);
        try {
            if (!selectedExisting) {
                await sessionService.createShortUrlAlias(slug, slug);
            }
            const res = await sessionService.assignShortUrl(slug, session.sessionId);
            const assignedSlug = res.data?.alias?.slug ?? slug;
            const assignedLabel = (res.data?.alias?.label ?? slug) || null;
            onAssigned(session.sessionId, assignedSlug, assignedLabel);
            handleClose();
        } catch (err) {
            onError(err.message || 'Failed to assign short URL');
        } finally {
            setSaving(false);
        }
    };

    const handleUnassign = async () => {
        setUnassigning(true);
        try {
            await sessionService.unassignShortUrl(currentAlias);
            onUnassigned(session.sessionId);
            handleClose();
        } catch (err) {
            onError(err.message || 'Failed to unassign short URL');
        } finally {
            setUnassigning(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await sessionService.deleteShortUrlAlias(currentAlias);
            onDeleted(session.sessionId);
            setConfirmDeleteOpen(false);
            handleClose();
        } catch (err) {
            onError(err.message || 'Failed to delete short URL');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {currentAlias ? (
                <>
                    <Box component="span" sx={{
                        display: 'inline-flex', alignItems: 'center', height: 22, fontSize: 11.5,
                        fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', background: 'var(--surface-2)',
                        border: '1px solid var(--border)', borderRadius: 999, px: 1.1,
                        maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {displayName}
                    </Box>
                    <Tooltip title="Copy URL">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopied(buildShortUrl(currentAlias)); }} color="info">
                            <OpusIcon name="copy" size={15} />
                        </IconButton>
                    </Tooltip>
                </>
            ) : (
                <span style={{ color: 'var(--text-dim)' }}>—</span>
            )}

            {isElevated && (
                <Tooltip title={currentAlias ? 'Change short URL' : 'Assign short URL'}>
                    <IconButton size="small" onClick={handleOpen} color="info">
                        <OpusIcon name="edit" size={15} />
                    </IconButton>
                </Tooltip>
            )}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClick={(e) => e.stopPropagation()}
            >
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-dim)' }}>
                        {currentAlias ? 'Reassign Short URL' : 'Assign Short URL'}
                    </Typography>

                    {currentAlias && (
                        <Typography sx={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                            Current: <Box component="span" sx={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{displayName}</Box>
                        </Typography>
                    )}

                    {/* Pick from existing pool */}
                    <FormControl size="small" fullWidth>
                        <InputLabel>Existing Short URLs</InputLabel>
                        <Select
                            value={selectedExisting}
                            label="Existing Short URLs"
                            onChange={(e) => { setSelectedExisting(e.target.value); setNewAlias(''); }}
                            disabled={loadingAliases}
                            endAdornment={loadingAliases ? <CircularProgress size={14} sx={{ mr: 3 }} /> : null}
                        >
                            <MenuItem value=""><em>— none —</em></MenuItem>
                            {existingAliases.map((a) => (
                                <MenuItem key={a.slug} value={a.slug} sx={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                                    {a.label || a.slug}
                                    {a.slug === currentAlias && (
                                        <Typography variant="caption" sx={{ ml: 1, color: 'var(--text-dim)' }}>(current)</Typography>
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider>
                        <Typography sx={{ fontSize: 10, color: 'var(--text-dim)' }}>OR CREATE NEW</Typography>
                    </Divider>

                    <TextField
                        size="small"
                        label="New Short URL"
                        placeholder="e.g. gallery-1"
                        value={newAlias}
                        onChange={(e) => { setNewAlias(e.target.value); setSelectedExisting(''); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleAssign()}
                        fullWidth
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {currentAlias && (
                                <>
                                    <Button size="small" color="error" variant="outlined" onClick={handleUnassign} disabled={unassigning || saving || deleting}>
                                        {unassigning ? <CircularProgress size={14} color="error" /> : 'Unassign'}
                                    </Button>
                                    <Button size="small" color="error" variant="outlined" onClick={() => setConfirmDeleteOpen(true)} disabled={unassigning || saving || deleting}>
                                        Delete
                                    </Button>
                                </>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" color="info" variant="outlined" onClick={handleClose}>Cancel</Button>
                            <Button size="small" variant="contained" color="seafoamGreen" onClick={handleAssign} disabled={saving || unassigning || (!selectedExisting && !newAlias.trim())}>
                                {saving ? <CircularProgress size={14} /> : 'Assign'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Popover>

            <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Short URL</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to permanently delete short URL{' '}
                        <strong>{displayName}</strong>?
                        {currentAlias && ' It will be unassigned from this session first.'}
                        {' '}This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ─── Session Management Page ──────────────────────────────────────────────────
const SessionManagement = () => {
    const navigate = useNavigate();
    const { userInfo } = useContext(AuthContext);
    const { can } = usePermissions();
    const alert = useAlert();

    const canViewOrgSessions = can('sessions.read.org');
    const canCreateSession = can('sessions.create');
    const canDeleteSession = can(['sessions.delete', 'sessions.delete.any']);
    const canEditSession = can(['sessions.update', 'sessions.update.any']);
    const canManageShortUrls = can('shorturls.aliases.manage');
    // Configured Maestro setup fields — shared by the create and edit dialogs.
    const maestroFields = useSessionFields();

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
    const [filters, setFilters] = useState({ flavour: '' });
    const [searchText, setSearchText] = useState('');

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        sessionName: '', applicationFlavour: 'aedrive', rendererId: '', fixtureId: '', fixtureName: '',
        fixtureStartTime: '', fixtureEndTime: '',
    });
    const [createErrors, setCreateErrors] = useState({});
    const [createMaestro, setCreateMaestro] = useState(() => maestroFormFromSession(null));
    const [createLoading, setCreateLoading] = useState(false);
    const createMaestroFields = fieldsForFlavour(maestroFields, createForm.applicationFlavour);
    const [renderers, setRenderers] = useState([]);
    const [dropdownsLoading, setDropdownsLoading] = useState(false);
    const [rendererStatusMap, setRendererStatusMap] = useState({});
    // Raw org renderer list from enrichRendererStatuses — reused by the edit dialog.
    const [orgRenderers, setOrgRenderers] = useState([]);
    const [fixtureNameMap, setFixtureNameMap] = useState({});

    // Browse state for the PLS competition/season picker in the create-session
    // dialog. Persisted per-machine like SceneControls' equivalent state, under
    // a DISTINCT key prefix (dashboard:sessionCreate:*) so this modal's
    // selection does not clobber (or get clobbered by) Scene Controls' own
    // dashboard:plsCompetitionId/dashboard:plsSeasonId keys.
    const [plsCompetitions, setPlsCompetitions] = useState([]);
    const [plsCompetitionId, setPlsCompetitionIdRaw] = useState(() => {
        try { return localStorage.getItem('dashboard:sessionCreate:plsCompetitionId') || PLS_COMPETITION_ID; }
        catch { return PLS_COMPETITION_ID; }
    });
    const [plsSeasons, setPlsSeasons] = useState([]);
    const [plsSeasonId, setPlsSeasonIdRaw] = useState(() => {
        try { return localStorage.getItem('dashboard:sessionCreate:plsSeasonId') || PLS_SEASON_ID; }
        catch { return PLS_SEASON_ID; }
    });

    const setPlsCompetitionId = useCallback((id) => {
        setPlsCompetitionIdRaw(id);
        try { localStorage.setItem('dashboard:sessionCreate:plsCompetitionId', id); } catch { /* ignore */ }
        // Changing competition invalidates the current fixture pick — the
        // fixture belonged to the old competition/season pairing.
        setCreateForm((f) => ({ ...f, fixtureId: '', fixtureName: '', fixtureStartTime: '', fixtureEndTime: '' }));
    }, []);
    const setPlsSeasonId = useCallback((id) => {
        setPlsSeasonIdRaw(id);
        try { localStorage.setItem('dashboard:sessionCreate:plsSeasonId', id); } catch { /* ignore */ }
        setCreateForm((f) => ({ ...f, fixtureId: '', fixtureName: '', fixtureStartTime: '', fixtureEndTime: '' }));
    }, []);

    // Rebuilds whenever the browsed competition/season changes — previously
    // this was built once with the two hard-coded constants and never rebuilt.
    const fixtureCatalogue = useMemo(() => createDdsPlsCatalogue({
        client: getDDSClient(),
        competitionId: plsCompetitionId,
        seasonId: plsSeasonId,
    }), [plsCompetitionId, plsSeasonId]);

    // Bumped to fetch competitions/seasons again — opening the create dialog
    // with an empty list retries, since DDS answers a failed org lookup with
    // an empty list rather than an error.
    const [pickerReload, setPickerReload] = useState(0);

    // Discover PLS competitions for the dialog's Competition dropdown.
    // Reconcile the persisted selection: keep it if still valid, else fall
    // back to the first competition DDS returns.
    useEffect(() => {
        let cancelled = false;
        fixtureCatalogue.getCompetitions()
            .then((comps) => {
                if (cancelled || !Array.isArray(comps)) return;
                setPlsCompetitions(comps);
                setPlsCompetitionIdRaw((prev) => {
                    if (prev && comps.some((c) => c.id === prev)) return prev;
                    const next = comps[0]?.id || null;
                    try { if (next) localStorage.setItem('dashboard:sessionCreate:plsCompetitionId', next); } catch { /* ignore */ }
                    return next;
                });
            })
            .catch(() => { if (!cancelled) setPlsCompetitions([]); });
        return () => { cancelled = true; };
    }, [fixtureCatalogue, pickerReload]);

    // Discover seasons for the currently browsed competition. Prefer the
    // active season ("yes"), else the first; keep a still-valid persisted pick.
    useEffect(() => {
        if (!plsCompetitionId) { setPlsSeasons([]); return undefined; }
        let cancelled = false;
        fixtureCatalogue.getSeasons(plsCompetitionId)
            .then((seasons) => {
                if (cancelled || !Array.isArray(seasons)) return;
                setPlsSeasons(seasons);
                setPlsSeasonIdRaw((prev) => {
                    if (prev && seasons.some((s) => s.id === prev)) return prev;
                    const active = seasons.find((s) => String(s.status).toLowerCase() === 'yes');
                    const next = (active || seasons[0])?.id || null;
                    try { if (next) localStorage.setItem('dashboard:sessionCreate:plsSeasonId', next); } catch { /* ignore */ }
                    return next;
                });
            })
            .catch(() => { if (!cancelled) setPlsSeasons([]); });
        return () => { cancelled = true; };
    }, [fixtureCatalogue, plsCompetitionId, pickerReload]);

    // Build a fixtureId -> label map so the session list can show fixture
    // names for sessions that only stored a fixtureId (older sessions / other
    // creation paths that didn't persist fixtureName). Scoped to whichever
    // competition/season is currently browsed in the create dialog — legacy
    // rows outside that pairing fall back to raw fixtureId in the grid.
    useEffect(() => {
        fixtureCatalogue.listFixtures()
            .then((fixtures) => {
                const map = {};
                for (const fx of fixtures) {
                    if (fx.id) map[fx.id] = fx.label;
                }
                setFixtureNameMap(map);
            })
            .catch(() => { /* fall back to raw fixtureId in the list */ });
    }, [fixtureCatalogue]);

    // Delete dialog — holds the full session row so the name can be shown
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Edit dialog (name / renderer / fixture)
    const [editTarget, setEditTarget] = useState(null);

    // DataGrid pagination model
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

    // Fetches the org's live renderers once and maps every session's rendererId
    // against that set — never calls GET /v1/renderers/:id for an individual
    // renderer, so a deleted renderer's id is never requested at all (it's
    // just absent from the org-wide list).
    const enrichRendererStatuses = useCallback(async (sessionRows) => {
        const uniqueIds = [...new Set(sessionRows.map((s) => s.rendererId).filter(Boolean))];
        if (!uniqueIds.length) return;
        try {
            const orgField = userInfo?.data?.organization_id;
            const orgId = (typeof orgField === 'object' && orgField !== null) ? orgField._id : orgField;
            if (!orgId) return;

            const res = await apiService.getRenderersByOrganization(orgId);
            const liveRenderers = res?.data?.renderers || [];
            setOrgRenderers(liveRenderers);
            const liveById = new Map(liveRenderers.map((r) => [r.renderer_id || r.id, r]));

            const map = {};
            uniqueIds.forEach((id) => {
                const renderer = liveById.get(id);
                if (!renderer) {
                    map[id] = { deleted: true };
                    return;
                }
                map[id] = {
                    name: renderer.renderer_name || renderer.name || null,
                    ownerLocked: renderer.owner_lock?.locked ?? false,
                    lockedByName: renderer.owner_lock?.locked_by_name ?? null,
                };
            });
            setRendererStatusMap(map);
        } catch (err) {
            console.error('[SessionManagement] enrichRendererStatuses failed:', err);
            // Intentionally silent — status cells degrade to showing nothing
        }
    }, [userInfo]);

    const fetchSessions = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                ...(filters.flavour && { flavour: filters.flavour }),
            };
            const fn = canViewOrgSessions ? sessionService.listOrgSessions : sessionService.listMySessions;
            const res = await fn(params);
            const sessionData = res.data || [];
            setSessions(sessionData);
            setPagination((prev) => ({
                ...prev,
                total: res.meta?.total ?? 0,
                totalPages: res.meta?.totalPages ?? 1,
            }));
            enrichRendererStatuses(sessionData); // non-blocking; does not delay table render
        } catch (err) {
            console.error('Error fetching sessions', err);
            showSnackbar('Error loading sessions', 'error');
        } finally {
            setLoading(false);
        }
    }, [paginationModel.page, paginationModel.pageSize, filters.flavour, canViewOrgSessions, enrichRendererStatuses]);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    const showSnackbar = (message, severity = 'success') => {
        if (severity === 'error') alert.error(message);
        else if (severity === 'warning') alert.warning(message);
        else alert.success(message);
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!');
    };

    const handleShortUrlAssigned = (sessionId, shortUrl, shortUrlLabel) => {
        setSessions((prev) => prev.map((s) => s.sessionId === sessionId ? { ...s, shortUrl, shortUrlLabel } : s));
        showSnackbar('Short URL assigned!');
    };

    const handleShortUrlUnassigned = (sessionId) => {
        setSessions((prev) => prev.map((s) => s.sessionId === sessionId ? { ...s, shortUrl: null, shortUrlLabel: null } : s));
        showSnackbar('Short URL unassigned');
    };

    const handleShortUrlDeleted = (sessionId) => {
        setSessions((prev) => prev.map((s) => s.sessionId === sessionId ? { ...s, shortUrl: null, shortUrlLabel: null } : s));
        showSnackbar('Short URL deleted');
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await sessionService.deleteSession(deleteTarget.sessionId);
            showSnackbar('Session deleted');
            setDeleteTarget(null);
            fetchSessions();
        } catch (err) {
            showSnackbar(err.message || 'Error deleting session', 'error');
            setDeleteTarget(null);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleEditSaved = () => {
        setEditTarget(null);
        showSnackbar('Session updated');
        fetchSessions(); // re-enriches renderer status for a newly assigned renderer
    };

    const handleOpenCreate = async () => {
        setCreateOpen(true);
        if (!plsCompetitions.length || !plsSeasons.length) setPickerReload((n) => n + 1);
        setDropdownsLoading(true);
        try {
            const orgField = userInfo?.data?.organization_id;
            const orgId = (typeof orgField === 'object' && orgField !== null) ? orgField._id : orgField;
            const renderersRes = await apiService.getRenderersByOrganization(orgId).catch(() => null);
            setRenderers(renderersRes?.data?.renderers || renderersRes?.renderers || []);
        } catch (_) { } finally {
            setDropdownsLoading(false);
        }
    };

    const validateCreate = () => {
        const errs = {};
        const nameError = validateSessionName(createForm.sessionName);
        if (nameError) errs.sessionName = nameError;
        if (!createForm.applicationFlavour) errs.applicationFlavour = 'Required';
        // Operators reach the session's fixture through its renderer: a session
        // without one opens AeDrive and Aero with no fixture selected.
        if (!createForm.rendererId) errs.rendererId = 'Required';
        if (!createForm.fixtureId) errs.fixtureId = 'Required';
        if (supportsMaestro(createForm.applicationFlavour)) {
            const maestroErrs = validateMaestroForm(createMaestro, createMaestroFields);
            if (Object.keys(maestroErrs).length) errs.maestro = maestroErrs;
        }
        setCreateErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleCreateSubmit = async () => {
        if (!validateCreate()) return;
        setCreateLoading(true);
        try {
            const userData = userInfo?.data;
            const res = await sessionService.createSession({
                ownerUserId: userData?._id || userData?.id,
                ownerUserName: userData?.user_name || userData?.name,
                sessionName: createForm.sessionName.trim(),
                applicationFlavour: createForm.applicationFlavour,
                createdFrom: 'dashboard',
                fixtureId: createForm.fixtureId || null,
                fixtureName: createForm.fixtureName || null,
                fixtureStartTime: createForm.fixtureStartTime
                    ? new Date(createForm.fixtureStartTime).toISOString()
                    : null,
                fixtureEndTime: createForm.fixtureEndTime
                    ? new Date(createForm.fixtureEndTime).toISOString()
                    : null,
                rendererId: createForm.rendererId || null,
                accessMode: 'normal',
                // Names are stored alongside the ids so the session page can show
                // them without another DDS lookup.
                applicationDetails: {
                    competitionId: plsCompetitionId,
                    competitionName: plsCompetitions.find((c) => c.id === plsCompetitionId)?.name || null,
                    seasonId: plsSeasonId,
                    seasonName: plsSeasons.find((s) => s.id === plsSeasonId)?.name || null,
                },
                // Only sent when a setup is named — otherwise operators keep
                // their own saved Maestro settings.
                ...(supportsMaestro(createForm.applicationFlavour) && toMaestroPayload(createMaestro, createMaestroFields)
                    ? { maestro: toMaestroPayload(createMaestro, createMaestroFields) }
                    : {}),
            });
            setCreateOpen(false);
            setCreateForm({
                sessionName: '', applicationFlavour: 'aedrive', rendererId: '', fixtureId: '', fixtureName: '',
                fixtureStartTime: '', fixtureEndTime: '',
            });
            setCreateMaestro(maestroFormFromSession(null));
            setRenderers([]);
            if (res?.reused) {
                // The service reuses an Aether session for the same fixture, and
                // for Copilot the session already on the chosen renderer.
                const existingName = res.data?.sessionName || res.data?.sessionId;
                showSnackbar(
                    createForm.applicationFlavour === 'aether'
                        ? `A session already exists for this fixture ("${existingName}"). No new session was created.`
                        : `This renderer is already assigned to "${existingName}". No new session was created.`,
                    'warning',
                );
            } else {
                showSnackbar('Session created!');
            }
            fetchSessions();
        } catch (err) {
            showSnackbar(err.message || 'Error creating session', 'error');
        } finally {
            setCreateLoading(false);
        }
    };

    const displayedSessions = searchText.trim()
        ? sessions.filter((s) => {
            const q = searchText.toLowerCase();
            const resolvedFixtureName = s.fixtureName || fixtureNameMap[s.fixtureId];
            return (
                s.sessionName?.toLowerCase().includes(q) ||
                s.sessionId?.toLowerCase().includes(q) ||
                resolvedFixtureName?.toLowerCase().includes(q) ||
                s.fixtureId?.toLowerCase().includes(q)
            );
        })
        : sessions;

    // Client-side availability filter — list endpoint returns snake_case (formatRendererListItem)
    // Note: owner_lock is a "lock to me" access gate on an existing session for this renderer
    // (see Session_Service_SRS.md §5.3) — it does not affect eligibility for a NEW session.
    // A renderer already "in_use" is no longer excluded here: the backend allows
    // sharing a renderer across sessions whose fixture time windows don't overlap,
    // and rejects at submit time (FIXTURE_TIME_CLASH) if they do.
    const availableRenderers = renderers.filter((r) =>
        r.allocation_status === 'free' || r.allocation_status === 'reserved' || r.allocation_status === 'in_use'
    );

    const columns = [
        {
            field: 'sessionName',
            headerName: 'Session',
            flex: 2,
            valueGetter: (value, row) => getSessionDisplayName(row),
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', minWidth: 0 }}>
                    <Typography
                        onClick={() => navigate(`/dashboard/session-management/${params.row.sessionId}`)}
                        sx={{ fontSize: 13.5, fontWeight: 650, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                        {params.value}
                    </Typography>
                    {params.value !== params.row.sessionId && (
                        <Typography sx={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {params.row.sessionId}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'rendererId',
            headerName: 'Renderer',
            flex: 2,
            renderCell: (params) => {
                if (!params.value) {
                    return <Typography sx={{ fontSize: 13.5, color: 'var(--text-muted)' }}>—</Typography>;
                }
                const statusInfo = rendererStatusMap[params.value];
                const isLocked = statusInfo?.ownerLocked === true;
                const isDeleted = statusInfo?.deleted === true;
                if (isDeleted) {
                    return (
                        <Tooltip title="This renderer has been deleted">
                            <Typography sx={{ fontSize: 13.5, color: 'var(--coral)', fontStyle: 'italic' }}>
                                Renderer deleted
                            </Typography>
                        </Tooltip>
                    );
                }
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <Tooltip title={statusInfo?.name ? params.value : ''}>
                            <Typography
                                component="a"
                                href={`${process.env.REACT_APP_SCENE_RENDERER_URL}/?renderer=${params.value}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ fontSize: 13.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                {statusInfo?.name || params.value}
                            </Typography>
                        </Tooltip>
                        {isLocked && (
                            <Tooltip title={statusInfo?.lockedByName ? `Locked by ${statusInfo.lockedByName}` : 'Locked'}>
                                <Box sx={{ display: 'flex', color: 'var(--coral)', flexShrink: 0 }}>
                                    <OpusIcon name="lock" size={13} />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'fixtureName',
            headerName: 'Fixture',
            flex: 2,
            renderCell: (params) => (
                <Typography sx={{ fontSize: 13.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {params.value || fixtureNameMap[params.row.fixtureId] || params.row.fixtureId || '—'}
                </Typography>
            ),
        },
        {
            field: 'applicationFlavour',
            headerName: 'App',
            flex: 0.75,
            renderCell: (params) => (
                <Badge tone={FLAVOUR_TONE[params.value] || 'neutral'}>
                    {FLAVOUR_LABELS[params.value] || params.value || '—'}
                </Badge>
            ),
        },
        {
            field: 'shortUrl',
            headerName: 'Short URL',
            flex: 1.5,
            renderCell: (params) => (
                <ShortUrlCell
                    session={params.row}
                    isElevated={canManageShortUrls}
                    onAssigned={handleShortUrlAssigned}
                    onUnassigned={handleShortUrlUnassigned}
                    onDeleted={handleShortUrlDeleted}
                    onCopied={handleCopy}
                    onError={(msg) => showSnackbar(msg, 'error')}
                />
            ),
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            flex: 0.75,
            renderCell: (params) => params.value ? (
                <Tooltip title={new Date(params.value).toLocaleString()} arrow>
                    <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                        {new Date(params.value).toLocaleDateString()}
                    </Typography>
                </Tooltip>
            ) : '—',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 110,
            type: 'number',
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, height: '100%' }}>
                    {canEditSession && (
                        <Tooltip title="Edit name, renderer & fixture" arrow>
                            <IconButton size="small" color="info" onClick={() => setEditTarget(params.row)}>
                                <OpusIcon name="edit" size={18} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canDeleteSession && (
                        <Tooltip title="Delete Session" arrow>
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}>
                                <OpusIcon name="trash" size={18} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Grid container spacing={1} sx={{ height: '100vh' }}>
            <Grid size={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <DashboardBreadcrumbs />
                    {canCreateSession && (
                        <OpusButton variant="primary" icon="plus" onClick={handleOpenCreate}>
                            New Session
                        </OpusButton>
                    )}
                </Box>
            </Grid>

            <Grid size={12} sx={{ height: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100% - 67px)',
                        width: '100%',
                        p: 2,
                        background: 'var(--surface-1)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--r-lg)',
                        minHeight: 0,
                    }}
                >
                    {/* Filters */}
                    <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.75, mb: 1.5, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <OpusSearchInput value={searchText} onChange={setSearchText} placeholder="Search sessions…" width={260} />
                            <Typography sx={{ fontSize: 13, color: 'var(--text-dim)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {displayedSessions.length} of {pagination.total}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <OpusSelect
                                value={filters.flavour}
                                onChange={(v) => setFilters({ ...filters, flavour: v })}
                                width={130}
                                options={[
                                    { value: '', label: 'All apps' },
                                    { value: 'aedrive', label: 'AeDrive' },
                                    { value: 'aether', label: 'Aether' },
                                    { value: 'copilot', label: 'Copilot' },
                                    { value: 'monitor', label: 'Monitor' },
                                    { value: 'custom', label: 'Custom' },
                                ]}
                            />
                        </Box>
                    </Box>

                    {/* DataGrid */}
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        {loading && sessions.length === 0 ? (
                            <LoadingState rows={8} label="Loading sessions…" />
                        ) : (
                            <DataGrid
                                rows={displayedSessions}
                                columns={columns}
                                getRowId={(row) => row.sessionId}
                                loading={loading}
                                rowHeight={64}
                                columnHeaderHeight={44}
                                disableRowSelectionOnClick
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                paginationMode="server"
                                rowCount={pagination.total}
                                pageSizeOptions={[10, 20, 50]}
                                sx={{
                                    height: '100%',
                                    border: 'none',
                                    '& .MuiDataGrid-footerContainer': { borderTop: 'none' },
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Grid>

            {/* ─── Create Session Dialog ─── */}
            <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setRenderers([]); }} maxWidth="md" fullWidth>
                <DialogTitle>New Session</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
                        <TextField
                            autoFocus
                            size="small"
                            label="Session Name"
                            placeholder="e.g. Studio A — Matchday 12"
                            value={createForm.sessionName}
                            onChange={(e) => setCreateForm({ ...createForm, sessionName: e.target.value })}
                            error={!!createErrors.sessionName}
                            helperText={createErrors.sessionName}
                            inputProps={{ maxLength: SESSION_NAME_MAX_LENGTH }}
                            fullWidth
                        />
                        <Box sx={TWO_COLUMNS_SX}>
                            <FormControl fullWidth size="small" error={!!createErrors.applicationFlavour}>
                                <InputLabel>Application</InputLabel>
                                <Select
                                    value={createForm.applicationFlavour}
                                    label="Application"
                                    onChange={(e) => setCreateForm({
                                        ...createForm,
                                        applicationFlavour: e.target.value,
                                    })}
                                >
                                    <MenuItem value="aedrive">AeDrive</MenuItem>
                                    <MenuItem value="copilot">Copilot</MenuItem>
                                    <MenuItem value="monitor">Monitor</MenuItem>
                                    <MenuItem value="custom">Custom</MenuItem>
                                </Select>
                            </FormControl>
                            <Autocomplete
                                options={availableRenderers}
                                size="small"
                                getOptionLabel={(r) => r.renderer_name || r.name || r._id || r.id || ''}
                                value={availableRenderers.find((r) => (r._id || r.id) === createForm.rendererId) || null}
                                onChange={(_, v) => {
                                    const previous = availableRenderers.find((r) => (r._id || r.id) === createForm.rendererId);
                                    setCreateForm({ ...createForm, rendererId: v ? (v._id || v.id) : '' });
                                    setCreateMaestro((m) => retargetPreviewUrl(m, previewRendererUrlFor(previous), previewRendererUrlFor(v)));
                                }}
                                disabled={dropdownsLoading}
                                isOptionEqualToValue={(o, v) => (o._id || o.id) === (v._id || v.id)}
                                renderOption={(props, r) => {
                                    const isLocked = r.owner_lock?.locked === true;
                                    return (
                                        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography sx={{ fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {r.renderer_name || r.name || r._id || r.id}
                                            </Typography>
                                            {isLocked && (
                                                <Tooltip title={r.owner_lock?.locked_by_name ? `Locked by ${r.owner_lock.locked_by_name}` : 'Locked'}>
                                                    <Box sx={{ display: 'flex', color: 'var(--coral)', flexShrink: 0 }}>
                                                        <OpusIcon name="lock" size={13} />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Renderer"
                                        required
                                        error={!!createErrors.rendererId}
                                        helperText={createErrors.rendererId}
                                    />
                                )}
                            />
                        </Box>
                        <Box>
                            <PlsCompetitionFixturePicker
                                catalogue={fixtureCatalogue}
                                wide
                                competitions={plsCompetitions}
                                competitionId={plsCompetitionId}
                                onCompetitionChange={setPlsCompetitionId}
                                seasons={plsSeasons}
                                seasonId={plsSeasonId}
                                onSeasonChange={setPlsSeasonId}
                                selectedFixtureId={createForm.fixtureId}
                                onSelect={(id, label, option) => setCreateForm((f) => ({
                                    ...f,
                                    fixtureId: id || '',
                                    fixtureName: label || '',
                                    // Both times come straight from the fixture selector: start
                                    // is the fixture's kickoff time; end is the real match end
                                    // when DDS has already recorded one (post-match), otherwise
                                    // the catalogue's start+default-duration fallback (see
                                    // DdsPlsCatalogue._endDateTime) — never manually typed.
                                    fixtureStartTime: option?.startDateTime
                                        ? toDateTimeLocalValue(option.startDateTime)
                                        : '',
                                    fixtureEndTime: option?.endDateTime
                                        ? toDateTimeLocalValue(option.endDateTime)
                                        : '',
                                }))}
                            />
                            {createErrors.fixtureId && (
                                <Typography sx={{ color: 'error.main', fontSize: 12, mt: 0.5, ml: 1.5 }}>
                                    {createErrors.fixtureId}
                                </Typography>
                            )}
                        </Box>
                        {createForm.fixtureId && (
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <TextField
                                    fullWidth
                                    label="Fixture start time"
                                    type="datetime-local"
                                    InputLabelProps={{ shrink: true }}
                                    value={createForm.fixtureStartTime}
                                    InputProps={{ readOnly: true }}
                                    helperText="From selected fixture"
                                />
                                <TextField
                                    fullWidth
                                    label="Fixture end time"
                                    type="datetime-local"
                                    InputLabelProps={{ shrink: true }}
                                    value={createForm.fixtureEndTime}
                                    InputProps={{ readOnly: true }}
                                    helperText="From selected fixture (estimated if match hasn't concluded)"
                                />
                            </Box>
                        )}
                        {supportsMaestro(createForm.applicationFlavour) && (
                            <>
                                <Divider sx={{ my: 0.5 }} />
                                <MaestroSetupFields
                                    fields={createMaestroFields}
                                    value={createMaestro}
                                    onChange={setCreateMaestro}
                                    errors={createErrors.maestro}
                                    hasSuggestedPreview={Boolean(previewRendererUrlFor(
                                        availableRenderers.find((r) => (r._id || r.id) === createForm.rendererId),
                                    ))}
                                />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCreateOpen(false); setRenderers([]); }} variant="outlined" color="info" disabled={createLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleCreateSubmit} variant="outlined" color="seafoamGreen" disabled={createLoading} startIcon={createLoading ? <CircularProgress size={14} color="inherit" /> : null}>
                        {createLoading ? 'Creating…' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Edit Session Dialog ─── */}
            <EditSessionDialog
                maestroFields={maestroFields}
                open={Boolean(editTarget)}
                session={editTarget}
                knownRenderers={orgRenderers}
                knownCompetitions={plsCompetitions}
                knownSeasons={{ competitionId: plsCompetitionId, seasons: plsSeasons }}
                onClose={() => setEditTarget(null)}
                onSaved={handleEditSaved}
                onError={(msg) => showSnackbar(msg, 'error')}
            />

            {/* ─── Delete Confirmation Dialog ─── */}
            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'text.secondary' }}>
                        Are you sure you want to delete session{' '}
                        <Box component="span" sx={{ fontWeight: 650, color: 'var(--text)' }}>
                            {getSessionDisplayName(deleteTarget)}
                        </Box>
                        {deleteTarget?.sessionName && deleteTarget.sessionName !== deleteTarget.sessionId && (
                            <Box component="span" sx={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                                {' '}({deleteTarget.sessionId})
                            </Box>
                        )}
                        ? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} color="info" variant="outlined" disabled={deleteLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                        {deleteLoading ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default SessionManagement;
